import React from 'react';

import { injectable } from 'inversify';

import { Icon, IconColor } from '@vk-hr-tek/ui/Icon';
import { t } from '@vk-hr-tek/core/translations/t';

import { WidgetPartition } from '@app/gen/dashboard';
import { modules } from '@app/modules';

@injectable()
export class PartitionMapper {
  processPartitions(
    partitions: WidgetPartition[],
    role: 'company' | 'employee',
  ) {
    return partitions.map((partition) =>
      this.processPartition(partition, role),
    );
  }

  private processPartition(
    partition: WidgetPartition,
    role: 'company' | 'employee',
  ) {
    switch (partition.permission) {
      case 'absences':
        return {
          title: t(`modules.Absences.absences.${role}Title`),
          count: partition.counter,
          resource: modules.absences.resource,
          href: `/${role}/${modules.absences.basename}`,
          icon: modules.absences.icon,
        };
      case 'candidates':
        return {
          title: t(`modules.Candidates.candidates.${role}Title`),
          count: partition.counter,
          resource: modules.candidates.resource,
          href: `/${role}/${modules.candidates.basename}`,
          icon: modules.candidates.icon,
        };
      case 'competencies':
        return {
          title: t(`modules.Competencies.competencies.${role}Title`),
          count: partition.counter,
          resource: modules.competencies.resource,
          href: `/${role}/${modules.competencies.basename}`,
          icon: modules.competencies.icon,
        };
      case 'employees':
        return {
          title: t(`modules.Employees.employees.${role}Title`),
          count: partition.counter,
          resource: modules.employees.resource,
          href: `/${role}/${modules.employees.basename}`,
          icon: modules.employees.icon,
        };
      case 'events':
        return {
          title: t(`modules.Events.events.${role}Title`),
          count: partition.counter,
          resource: modules.events.resource,
          href: `/${role}/${modules.events.basename}`,
          icon: modules.events.icon,
        };
      case 'events_to_paper':
        return {
          title: t(`modules.Events.paper.${role}Title`),
          count: partition.counter,
          resource: modules.paper.resource,
          href: `/${role}/${modules.events.basename}/${modules.paper.basename}`,
          icon: ({ color = 'text.light.primary' }: { color?: IconColor }) => (
            <Icon name="globe_outline" size="24" color={color} />
          ),
        };
      case 'organization':
        return {
          title: t(`modules.Organization.organization.${role}Title`),
          count: partition.counter,
          resource: modules.structure.resource,
          href: `/${modules.structure.basename}`,
          icon: modules.structure.icon,
        };
      case 'payslips':
        return {
          title: t(`modules.Payslips.payslips.${role}Title`),
          count: partition.counter,
          resource: modules.payslips.resource,
          href: `/${role}/${modules.payslips.basename}`,
          icon: modules.payslips.icon,
        };
      case 'personal_profile':
        return {
          title: t(`modules.Personal.personal.${role}Title`),
          count: partition.counter,
          resource: modules.personal.resource,
          href: `/${modules.personal.basename}`,
          icon: modules.personal.icon,
        };
      case 'policies':
        return {
          title: t(`modules.Policy.policy.${role}Title`),
          count: partition.counter,
          resource: modules.policy.resource,
          href: `/${role}/${modules.policy.basename}`,
          icon: modules.policy.icon,
        };
      case 'settings_attorneys':
        return {
          title: t(`modules.Settings.attorneys.${role}Title`),
          count: partition.counter,
          resource: modules.attorneys.resource,
          href: `/${role}/${modules.settings.basename}/${modules.attorneys.basename}`,
          icon: modules.settings.icon,
        };
      case 'settings_attorneys_users':
        return {
          title: t(`modules.Settings.attorneys-users.${role}Title`),
          count: partition.counter,
          resource: modules.attorneys_users.resource,
          href: `/${role}/${modules.settings.basename}/${modules.attorneys_users.basename}`,
          icon: modules.settings.icon,
        };
      case 'settings_company':
        return {
          title: t(`modules.Settings.company.${role}Title`),
          count: partition.counter,
          resource: modules.company.resource,
          href: `/${role}/${modules.settings.basename}/${modules.company.basename}`,
          icon: modules.settings.icon,
        };
      case 'settings_event_types':
        return {
          title: t(`modules.Settings.settings.${role}Title`),
          count: partition.counter,
          resource: modules.dropdown_settings.resource,
          href: `/${role}/${modules.dropdown_settings.basename}`,
          icon: modules.dropdown_settings.icon,
        };
      case 'settings_groups':
        return {
          title: t(`modules.Settings.groups.${role}Title`),
          count: partition.counter,
          resource: modules.groups.resource,
          href: `/${role}/${modules.settings.basename}/${modules.groups.basename}`,
          icon: modules.settings.icon,
        };
      case 'settings_substitutes':
        return {
          title: t(`modules.Settings.substitutes.${role}Title`),
          count: partition.counter,
          resource: modules.substitutes.resource,
          href: `/${role}/${modules.settings.basename}/${modules.substitutes.basename}`,
          icon: modules.settings.icon,
        };
      case 'settings_templates':
        return {
          title: t(`modules.Settings.templates.${role}Title`),
          count: partition.counter,
          resource: modules.templates.resource,
          href: `/${role}/${modules.settings.basename}/${modules.templates.basename}`,
          icon: modules.settings.icon,
        };
      case 'settings_unep_types':
        return {
          title: t(`modules.Settings.unep-types.${role}Title`),
          count: partition.counter,
          resource: modules.sign_types.resource,
          href: `/${role}/${modules.settings.basename}/${modules.sign_types.basename}`,
          icon: modules.settings.icon,
        };
      case 'settings_vacation':
        return {
          title: t(`modules.Settings.vacations.${role}Title`),
          count: partition.counter,
          resource: modules.settings_vacations.resource,
          href: `/${role}/${modules.settings.basename}/${modules.settings_vacations.basename}`,
          icon: modules.settings.icon,
        };
      case 'vacation':
        return {
          title: t(`modules.Vacations.vacations.${role}Title`),
          count: partition.counter,
          resource: modules.vacations.resource,
          href: `/${role}/${modules.vacations.basename}`,
          icon: modules.vacations.icon,
        };
      default:
        return {
          title: '',
        };
    }
  }
}
